import { z } from 'zod';
import {
  bandwidthLevelSchema,
  linkReportSchema,
  meshTransportSchema,
  type BandwidthLevel,
} from './agent.js';

/** Connettività (ADR 0026): the stations of an organization, their links, the Meshtastic nodes. */
export const STATION_KINDS = ['pca', 'coc', 'headquarters', 'vehicle'] as const;
export const stationKindSchema = z.enum(STATION_KINDS);
export type StationKind = z.infer<typeof stationKindSchema>;

export const stationLinkSchema = linkReportSchema.extend({
  measuredAt: z.string().datetime(),
});
export type StationLink = z.infer<typeof stationLinkSchema>;

export const stationSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  kind: stationKindSchema,
  mission: z.object({ id: z.string().uuid(), code: z.string() }).nullable(),
  /** The last heartbeat; null: the agent never called. */
  lastSeenAt: z.string().datetime().nullable(),
  agent: z.object({ version: z.string(), platform: z.string(), hostname: z.string() }).nullable(),
  level: bandwidthLevelSchema,
  links: z.array(stationLinkSchema),
  mesh: z.object({ transport: meshTransportSchema.nullable(), connected: z.boolean() }).nullable(),
  /** Where the station's gateway node publishes, if it speaks MQTT to the server. */
  mqtt: z.object({ username: z.string(), rootTopic: z.string() }),
});
export type Station = z.infer<typeof stationSchema>;

export const stationCreateSchema = z.object({
  name: z.string().trim().min(2).max(60),
  kind: stationKindSchema,
  missionId: z.string().uuid().nullable().default(null),
});
export type StationCreate = z.input<typeof stationCreateSchema>;

export const stationUpdateSchema = stationCreateSchema.partial();
export type StationUpdate = z.input<typeof stationUpdateSchema>;

/** Shown once: the agent's key and the broker account of the gateway node. */
export const stationCredentialsSchema = z.object({
  station: stationSchema,
  agentKey: z.string(),
  mqtt: z.object({
    host: z.string(),
    port: z.number().int(),
    username: z.string(),
    password: z.string(),
    rootTopic: z.string(),
  }),
});
export type StationCredentials = z.infer<typeof stationCredentialsSchema>;

export const meshNodeSchema = z.object({
  id: z.string().uuid(),
  /** `!1a2b3c4d`. */
  nodeId: z.string(),
  name: z.string().nullable(),
  shortName: z.string().nullable(),
  hardware: z.string().nullable(),
  device: z.object({ id: z.string().uuid(), code: z.string(), name: z.string() }).nullable(),
  lastHeardAt: z.string().datetime(),
  batteryPct: z.number().int().nullable(),
  voltage: z.number().nullable(),
  snr: z.number().nullable(),
  rssi: z.number().int().nullable(),
  channelUtilization: z.number().nullable(),
  position: z.object({ lng: z.number(), lat: z.number() }).nullable(),
  /** Through which station it was heard; null: straight over MQTT. */
  via: z.object({ id: z.string().uuid(), code: z.string() }).nullable(),
});
export type MeshNode = z.infer<typeof meshNodeSchema>;

export const meshNodeBindSchema = z.object({ deviceId: z.string().uuid().nullable() });
export type MeshNodeBind = z.infer<typeof meshNodeBindSchema>;

/** What a thin link pauses (ADR 0026): the heavy features, by level. */
export const BANDWIDTH_FEATURES = ['drone_video', 'radar_animation', 'video_attachments'] as const;
export type BandwidthFeature = (typeof BANDWIDTH_FEATURES)[number];

const PAUSED: Record<BandwidthLevel, readonly BandwidthFeature[]> = {
  full: [],
  reduced: ['drone_video', 'radar_animation'],
  minimal: ['drone_video', 'radar_animation', 'video_attachments'],
  offline: [...BANDWIDTH_FEATURES],
};

export function pausedFeatures(level: BandwidthLevel): readonly BandwidthFeature[] {
  return PAUSED[level];
}

export function isPaused(level: BandwidthLevel, feature: BandwidthFeature): boolean {
  return PAUSED[level].includes(feature);
}
